/**
 * Auth - internal Entra-only. Restricted to @claridynamics.com at the API layer.
 *
 * This is the standalone Lab Intake app, NOT the multi-tenant reports portal. There is no
 * signed-link path, no guest path, no USERS allowlist. A user either holds a valid Entra access
 * token for this app's API scope (and a @claridynamics.com identity) or sees nothing.
 *
 * The bearer rides the X-Cdx-Bearer CUSTOM header (Azure SWA rewrites Authorization on managed
 * Functions routes - prod-confirmed on the portal 2026-06-12). See lib/apiClient.ts.
 */
import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? 'dev-mode-client-id';
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? 'common';

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: `${window.location.origin}/login/callback`,
    postLogoutRedirectUri: `${window.location.origin}/login`,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // Tab-scoped, cleared on tab close. Tokens never hit disk. storeAuthStateInCookie keeps only
    // the short-lived nonce/state so acquireTokenSilent's hidden-iframe renewal works in browsers
    // that partition iframe storage (the portal's proven pairing).
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: true,
  },
  system: {
    iframeHashTimeout: 6_000,
    loadFrameTimeout: 10_000,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

let initPromise: Promise<void> | null = null;
export async function ensureMsalInitialized(): Promise<void> {
  if (!initPromise) initPromise = msalInstance.initialize();
  return initPromise;
}

/** Minimum scopes at login. */
export const loginRequest = {
  scopes: ['User.Read', 'profile', 'openid', 'email'],
};

/**
 * API token request - the scope used to mint the Entra access token the API accepts as the bearer
 * (sent via X-Cdx-Bearer). This app's own exposed API scope; the SPA is pre-authorized against the
 * same app so consent is implicit. In prod, VITE_ENTRA_CLIENT_ID is the Lab Intake app id.
 */
export const apiTokenRequest = {
  scopes: [`api://${clientId}/access_as_user`],
};

/** Dev bypass: on localhost with no configured client id, skip the Entra wall so dev is frictionless. */
export function isDevAuthBypass(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITE_ENTRA_CLIENT_ID;
}
